/**
 * LightningLayer.tsx
 * رموز صاعقة مبعثَرة فوق مناطق النشاط العاصفي المتوقَّع — **توقّعات لا بيانات لحظية**:
 * تُقرأ من نسيج K-index (المؤشّر الأرصادي القياسي لعدم الاستقرار الحراري-الرطوبي)،
 * مولَّد مسبقاً لكل ساعات التوقّع 0-120 من كل نموذج، مخزَّن عبر Cloudflare مع بقية
 * الطبقات — راجع backend/scripts/derive_thunder_indices.py.
 *
 * K = (T850 - T500) + Td850 - (T700 - Td700)  °م
 *   < 20   بلا عواصف رعدية
 *   20..25 عواصف معزولة محتملة  ← عتبتنا الدنيا 22 (K_THRESHOLD)
 *   26..30 عواصف متفرّقة
 *   31..35 عواصف كثيرة
 *   > 35   عواصف واسعة الانتشار
 *
 * لماذا K-index لا الصيغة السابقة o-index: o-index كانت مركّباً محلياً (CAPE × الهطول
 * الحملي × ...) بأس كسريّ ذاتيّ الوضع، تختلف قراءته بين النماذج لأن ECMWF/ICON لا
 * ينشران CAPE سطحياً حقيقياً ولا CPRAT في open data (يستعملان MUCAPE/CAPE_ML والهطول
 * الكلي كتقريبات). K-index على العكس: تعريف قياسي واحد يُحسب في كل النماذج من نفس
 * مستويات الضغط 850/700/500 بنفس المعادلة، فيتّفق سلوكه بين النماذج ويطابق ما تعرضه
 * الخدمات الأرصادية الفعلية (meteologix, wxcharts) عند نفس العتبة.
 *
 * البرق يُشترط له مع K ≥ 26: **هطول متوقّع فعلاً** (لا مجرّد قابلية) — العاصفة الرعدية
 * تحتاج سحاباً ركامياً نشطاً لا هواءً غير مستقرٍّ صافياً، فبدون هذا الشرط تظهر صواعق فوق
 * صحاري حارّة في أشدّ أوقات النهار بلا أي غيمة، وهو ما تراه الآن على الخريطة.
 *
 * لماذا لا بيانات حيّة: كانت هذه الطبقة تعرض ومضات Blitzortung.org الحقيقية لحظياً
 * عبر WebSocket، لكن شروط استخدام Blitzortung تمنع الاستخدام التجاري صراحةً بلا ترخيص
 * (ولا يقدّمون ترخيصاً تجارياً أصلاً). التوقّعات هنا مصدرها بياناتنا الحرّة الموجودة
 * فعلاً (GFS)، فلا مشكلة ترخيص إطلاقاً، وتُعرض عبر كل ساعات الشريط الزمني (٥ أيام) لا
 * "الآن" فقط — مطابقة لبقية الطبقات (أمطار/رياح/حرارة) تماماً.
 *
 * الأسلوب البصري — **سرب يملأ الخليّة العاصفية**، كما يفعل Windy فوق إثيوبيا وجنوب
 * السودان في اللقطة المرجعية (تباعد نحو 15-20px على عرض 1919px). البناء على مرحلتين
 * تفصلان بين سؤالين مختلفين:
 *
 *  ❶ **أين النشاط؟** شبكة مرشّحات على الشاشة (`forEachScatterCell`) تجمع مواقع النشاط.
 *    تحت z6 تُمسح رقعة كل خليّة بدقّة الراستر (`maxScalarInBox`) لا بعيّنة نقطية، فلا
 *    تُفقد خليّة عاصفية انكمشت عند التصغير إلى بضعة بكسلات، ويقع الرمز على ذروة النشاط
 *    نفسها لا على منتصف رقعة أغلبها صافٍ.
 *
 *  ❷ **أيّها يُرسم؟** فصلٌ بأدنى مسافة على الشاشة (MIN_SEPARATION_PX)، الأقوى أوّلاً.
 *    يمنع تكديس رمزين على نقطة واحدة ويضبط أدنى تباعد داخل السرب، والترتيب بالشدّة
 *    يضمن أن تظهر أقوى العواصف أوّلاً حين يبلغ العدد سقفه.
 *
 * وكون المسافة بالبكسل لا بالدرجات مقصود: مظهر ثابت على الشاشة عند أي تكبير، كرموز Windy.
 */
import { useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { rasterSampler } from '../../services/rasterSampler';
import { forEachScatterCell } from './utils/iconScatter';
import { PRECIP_ACCUMULATION_HOURS } from './hoverValueFormat';
import { K_THRESHOLD, MIN_STORM_MM, MIN_STORM_TEMP_C } from './thunderCriteria';
import { BOLT_PATH_D, BOLT_UNITS } from './lightningGlyph';

/** تباعد شبكة **المرشّحات** على الشاشة بالبكسل.
 *
 * لم تعد هذه الشبكة تقرّر شكل النتيجة: هي مجرّد ماسح يجمع مواقع النشاط المرشّحة، ثم
 * يتكفّل الفصل أدناه (MIN_SEPARATION_PX) باختيار من يُرسم منها. لذلك تباعد واحد يكفي
 * لكل مستويات التكبير — أدقّ من الفصل بكثير كي لا تفوتنا ذروة بين خليّتين، وليس أدقّ
 * ممّا يلزم فتُهدر القراءات. */
const CANDIDATE_CELL_PX = 14;

/**
 * التكبير الذي تصير عنده خليّة الشاشة أصغر من خليّة الراستر، فتكفي العيّنة النقطية.
 * تحته نمسح رقعة كل خليّة بدقّة الراستر (maxScalarInBox) كي لا تُفقد خليّة عاصفية
 * انكمشت إلى بضعة بكسلات.
 */
const BOX_PROBE_MAX_ZOOM = 6;

/**
 * أدنى مسافة على الشاشة بين صاعقتين مرسومتين — بالبكسل.
 *
 * **قياس من المرجع لا تقدير.** جُرِّبت 60px بناءً على لقطة سابقة بدت فيها صواعق Windy
 * متفرّقة، وكان ذلك خطأ في قراءة المرجع: Windy **يملأ** الخليّة العاصفية بسرب كثيف —
 * واضح فوق إثيوبيا وجنوب السودان في اللقطة المرجعية، حيث تتباعد رموزه نحو 15-20px على
 * عرض 1919px. الفصل هنا ليس أداة تخفيف إذن، بل حارس ضدّ **تكديس** رمزين على نقطة
 * واحدة، وضبط لأدنى تباعد يُبقي الرموز مقروءة داخل السرب.
 *
 * والقيمة بالبكسل لا بالدرجات عمداً: المطلوب ثبات المظهر على الشاشة عند أي تكبير،
 * تماماً كرموز Windy.
 */
const MIN_SEPARATION_PX = 13;

/** عتبة K-index الدنيا لاعتبار الخليّة "نشاط عاصفي حقيقي".
 *
 * 22 لا 26: التصنيف القياسي (Miller 1972) يبدأ عند 20 ("عواصف معزولة محتملة")،
 * وWindy يظهر بروقاً على تلك الخلايا الحدّية. رفع العتبة إلى 26 كان يُخفي الحزام
 * الاستوائي الرطب بأكمله (إثيوبيا/اليمن/وسط أفريقيا) لأن K هناك بين 22..25 غالباً —
 * قابلية عواصف حقيقية لا استقرار كامل. 22 = "عواصف معزولة" في التصنيف نفسه، ومع
 * اشتراط ≥ 0.5 مم/3س لا يُضاء إلا ما فيه هطول متوقّع فعلاً، فلا صواعق كاذبة في
 * قابلية جافّة. */
// المصدر الآن thunderCriteria كي تشترك اللوحة والخريطة في العتبة نفسها.
/** سقف المرشّحات المجموعة قبل الفصل — حارس تكلفة لا معياراً بصرياً. */
const MAX_CANDIDATES = 20000;
/** لا برق إلا فوق هطول **مرئي فعلاً** على الخريطة.
 *
 * 0.1 مم كانت تمرّ رغم أنها تُرسم شفافة/باهتة تماماً، فتظهر صواعق فوق ما يبدو للعين
 * أرضاً جافّة — وهو ما رُصد بالضبط. 1.5 مم كانت صارمة أكثر من اللازم فتُسقِط خلايا
 * رعدية حقيقية بهطول خفيف؛ 0.5 مم/3س يبقى فوق حدّ "الجاف" ويسمح بالزخّات الخفيفة. */
// MIN_STORM_MM و MIN_STORM_TEMP_C كلاهما من thunderCriteria (نفس السبب أعلاه).
/** سقف العناصر على الشاشة لحماية التفاعل مع الخريطة.
 *
 * 4500 لا 2200: عند التصغير الواسع (كل قارّة) يُنتج الحزام الاستوائي وحده ~4000
 * مرشّح مؤهّل (~40% من الشاشة × 10,000 موضع ممكن بتباعد 13px). سقف 2200 كان يقصّ
 * أكثر من نصفهم فيبدو الحزام مخفَّفاً بينما Windy يعرضه مكتظّاً. الترتيب بالشدّة
 * (Miller) يضمن أن يبقى الأقوى إن اضطرّ الحدّ للسريان.
 *
 * الأداء آمن عند 4500 عنصر: position() تُبقي الكلفة عند صفر أثناء السكون (حارس
 * المنظور)، وأثناء التحريك الخالص تعمل بتحويل واحد على الحاوية بدل 4500 كتابة، ولا
 * تُنفّذ إسقاطاً كاملاً إلّا عند تغيّر التكبير/الدوران — وهي أحداث لحظية لا مستمرّة. */
const MAX_MARKERS = 4500;
/** فوق هذا العدد يُطفأ الوميض — الحركة الكثيفة تثقل التركيب.
 *
 * 3000 لا 500: الوميض CSS animation (opacity) ينفّذه المتصفّح على مُركِّب الطبقات
 * بلا إعادة تخطيط ولا إعادة رسم بكسل — تكلفته حجم ثابت لكل عنصر منخفض جداً، بخلاف
 * كتابة left/top التي كانت تُجبر إعادة تخطيط للصفحة. بعد تحويل الموضع إلى transform
 * في position() لم يعد للحدّ سبب عند 500. رفعناه لتشمل حالة التصغير كاملةً حيث
 * الحزام الاستوائي يبلغ ~4000 صاعقة. فوق 3000 نبقى محافظين. */
const MAX_ANIMATED = 3000;

/** صاعقة صغيرة — أبيض موحّد بحدّ داكن للوضوح، نفس لون رمز الثلج (لون رمز واحد لكل
 * الأنواع كما Windy)، بحجم صغير يناسب التكرار الكثيف.
 *
 * **صورة خلفية لا SVG داخل الصفحة، والهالة مخبوزة داخلها لا `filter` في CSS** — وهذا
 * سبب بطء إعادة البناء عند التكبير/التصغير كلّه تقريباً. النسخة السابقة كانت تُنشئ لكل
 * صاعقة شجرةَ عناصر (div ▸ span ▸ svg ▸ path) وتضع على الوسطى ثلاثة `drop-shadow`.
 * عند سرب كامل (MAX_MARKERS) يعني ذلك تحليل ~١٫٣ ميغابايت HTML وبناء ١٨ ألف عقدة،
 * ثم **٤٥٠٠ تمريرة فلترة منفصلة** عند الرسم — كل واحدة تُرسم وحدها لأن الفلتر يخصّ
 * عنصراً بعينه. هنا الرمز عنوان بيانات واحد ثابت: المتصفّح يفكّه ويُنقّطه (بالهالة)
 * **مرّة واحدة** ويعيد استعمال النقطية نفسها في كل صاعقة، وكل صاعقة عنصر `div` واحد
 * بلا أبناء. الشكل النهائي مطابق: نفس المسار ونفس الظلال الثلاثة بنفس الألوان.
 *
 * الأبعاد: المسار في مدى 24 وحدة والرمز يُعرض 13px، فوحدة الهالة الزائدة (8 من كل
 * جهة) تجعل الصورة 40 وحدة = 13 × 40/24 بكسل — راجع BOLT_PX. وأنصاف أقطار الضبابية
 * محوَّلة بنفس النسبة: `drop-shadow(blur)` نصف قطرها = 2×stdDeviation بالبكسل. */
const BOLT_PX = (13 * 40) / BOLT_UNITS;
const BOLT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-8 -8 40 40">`
    + `<filter id="g" x="-50%" y="-50%" width="200%" height="200%" color-interpolation-filters="sRGB">`
    + `<feDropShadow dx="0" dy="0" stdDeviation="4.6" flood-color="#ffd648" flood-opacity="0.95"/>`
    + `<feDropShadow dx="0" dy="0" stdDeviation="1.85" flood-color="#ffffff" flood-opacity="0.9"/>`
    + `<feDropShadow dx="0" dy="1.85" stdDeviation="1.85" flood-color="#000000" flood-opacity="0.8"/>`
    + `</filter>`
    + `<path filter="url(#g)" fill="#ffffff" stroke="rgba(20,15,0,0.6)" stroke-width="1.3"`
    + ` d="${BOLT_PATH_D}"/>`
    + `</svg>`;
const BOLT_URL = `url("data:image/svg+xml,${encodeURIComponent(BOLT_SVG)}")`;

/**
 * دقّة تقسيم الساعة التي يُعاد عندها بناء السرب أثناء تحرّك الشريط الزمني.
 *
 * التقريب يحدّد **متى** نُعيد البناء لا **أيّ لحظة** نرسمها: المسح يقرأ النسبة الدقيقة من
 * `fractionRef`. فدور هذا العدد منع إعادة بناء لا تُغيّر شيئاً، لا تخشين الزمن.
 *
 * 30 لأنها أصغر عدد تقسمه خطوات التشغيل كلها: `useAutoplay` يتقدّم بـ `subSteps` خطوة
 * لكل ساعة، وهي 6 مع الطبقات العددية و10 مع محور القمر الصناعي (راجع NewWeatherMap).
 * 30/6 = 5 و30/10 = 3، فكل خطوة تشغيل تقع على قيمة تقريب متمايزة ⇒ **إعادة بناء واحدة
 * لكل خطوة، والفارق عن اللون صفر**. عدد لا يقبل القسمة عليهما (8 مثلاً) يدمج خطوتين
 * أحياناً فيعود تأخّر صغير — وهو ما نُصلحه أصلاً.
 *
 * ولا يزيد هذا كلفة سحب الشريط: هناك النسبة متّصلة، ويحدّ المعدّلَ تجميعُ rAF أدناه لا
 * هذا العدد. أثره الوحيد إسقاط الإعادات التي لا تُغيّر شيئاً.
 */
const FRACTION_STEPS = 30;

interface Props {
    enabled: boolean;
    timeIndex: number;
    /**
     * موضع الشريط داخل الساعة [0,1] — نفس القيمة التي تُمرَّر لطبقات الألوان كـ `blend`.
     *
     * بدونها كانت هذه الطبقة **العنصر الوحيد على الخريطة الذي يقفز بالساعة**: طبقات
     * الألوان تمزج نسيجَي الساعة والساعة التالية على الـ GPU فتتغيّر باستمرار مع حركة
     * الشريط، بينما كان السرب يُبنى من الإطار الصحيح وحده فيبقى على حالة الساعة N حتى
     * يقفز الفهرس. النتيجة أن اللون يكون قد أزاح المطر عن بقعة والبرق ما زال فوقها —
     * وهو التأخّر الذي يُرى صواعقَ فوق أرض بلا هطول أثناء الانتقال. ليست هذه حالة
     * تحميل عابرة بل فارق دائم عند كل نسبة بين الساعتين.
     */
    frameFraction: number;
    /** مصدر الهطول والبرق هو مجلد النموذج المختار حالياً، لا GFS ثابتاً. */
    modelName: string;
}

export function LightningLayer({ enabled, timeIndex, frameFraction, modelName }: Props) {
    const mapRef = useMapRef();
    const overlayRef = useRef<HTMLDivElement | null>(null);
    // `suffix` = دوران/حجم الرمز جاهزاً كذيل تحويل: يُلحق بالانتقال في كل وضعٍ بلا إعادة
    // تركيب نصّ، و`delay` مخزَّن كي لا يُكتب على العنصر إن لم يتغيّر عند إعادة الاستعمال.
    const markersRef = useRef<{ el: HTMLElement; lon: number; lat: number; suffix: string; delay: string }[]>([]);
    const rebuildRef = useRef<(() => void) | null>(null);
    const rafRef = useRef<number | null>(null);
    /** آخر لحظة بُني فيها السرب — يحدّ معدّل إعادة البناء بالزمن لا بعدد الخطوات. */
    const lastRebuildRef = useRef(0);
    /** متوسط متحرّك لكلفة بناء السرب (مللي) — أساس معايرة معدّل الملاحقة. */
    const buildMsRef = useRef(0);
    // حالة المنظور عند آخر وضعٍ للرموز، ومرساتان لكشف التحريك الخالص — راجع position.
    const viewRef = useRef<{ zoom: number; bearing: number; pitch: number; lng: number; lat: number } | null>(null);
    const anchorsRef = useRef<{
        a: { lon: number; lat: number; x: number; y: number };
        b: { lon: number; lat: number; x: number; y: number };
    } | null>(null);
    // أحدث إطار زمني عبر ref (يقرؤه build أدناه بلا إعادة تركيب المستمعات).
    const timeIndexRef = useRef(timeIndex);
    timeIndexRef.current = timeIndex;
    // ونسبة الموضع داخل الساعة معه: build يقرأ الاثنين فيرسم لحظة الشريط نفسها التي
    // يرسمها اللون. بلا ref كان علينا إعادة تركيب المستمعات مع كل إطار من إطارات المزج.
    const fractionRef = useRef(frameFraction);
    fractionRef.current = frameFraction;

    // ── تركيب التراكم مرّة واحدة ──────────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const container = map.getContainer();
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:10;overflow:hidden;';
        // الرمز وحجمه يُمرَّران متغيّرَي CSS مرّة واحدة على الحاوية: قاعدة واحدة في
        // 14-cyclone.css تطبّقهما على كل الصواعق، فلا نصّ نمط مكرّر على آلاف العناصر.
        overlay.style.setProperty('--bolt', BOLT_URL);
        overlay.style.setProperty('--bolt-px', `${BOLT_PX.toFixed(2)}px`);
        container.appendChild(overlay);
        overlayRef.current = overlay;

        /**
         * يضع الرموز في مواضعها — وهي أسخن دالّة في الطبقة: `render` في MapLibre يُطلق
         * في **كل إطار** ما دام هناك ما يتحرّك، وطبقة جسيمات الرياح تُبقيه يعمل باستمرار
         * نحو 60 مرّة في الثانية حتى والخريطة ساكنة تماماً. فمع سرب كثيف (حتى
         * MAX_MARKERS رمز) كانت النسخة السابقة تنفّذ عشرات آلاف الإسقاطات وكتابات
         * `left/top` في الثانية بلا أي تغيّر على الشاشة — وكتابة left/top على آلاف
         * العناصر المطلقة تُجبر إعادة تخطيط للصفحة في كل مرّة. هذا هو سبب ثقل التصفّح.
         *
         * ثلاثة مسارات مرتّبة من الأرخص إلى الأغلى:
         */
        const position = () => {
            const mapObj = mapRef.current;
            const host = overlayRef.current;
            if (!mapObj || !host) return;

            const zoom = mapObj.getZoom();
            const bearing = mapObj.getBearing();
            const pitch = mapObj.getPitch();
            const center = mapObj.getCenter();
            const view = viewRef.current;

            // ❶ لم يتغيّر شيء في المنظور: لا عمل إطلاقاً. هذا يلغي الكلفة المستمرّة
            //    الناتجة عن إطارات الجسيمات وهي الحالة الغالبة أثناء التصفّح.
            if (view
                && view.zoom === zoom && view.bearing === bearing && view.pitch === pitch
                && view.lng === center.lng && view.lat === center.lat) return;

            // ❷ تحريك خالص (التكبير/الدوران/الميل كما هي): إسقاط مركاتور عند تكبير ثابت
            //    إزاحةٌ موحّدة لكل النقاط، فيكفي تحريك الحاوية بدل إسقاط كل رمز. نتحقّق
            //    من ذلك بمرساتين متباعدتين بدل افتراضه: إن اختلفت إزاحتاهما فالتحويل ليس
            //    انتقالاً خالصاً ونسقط إلى المسار الكامل.
            const anchors = anchorsRef.current;
            if (view && anchors
                && view.zoom === zoom && view.bearing === bearing && view.pitch === pitch) {
                const a = mapObj.project([anchors.a.lon, anchors.a.lat]);
                const b = mapObj.project([anchors.b.lon, anchors.b.lat]);
                const dxA = a.x - anchors.a.x, dyA = a.y - anchors.a.y;
                const dxB = b.x - anchors.b.x, dyB = b.y - anchors.b.y;
                if (Math.abs(dxA - dxB) < 0.5 && Math.abs(dyA - dyB) < 0.5) {
                    host.style.transform = `translate(${dxA}px, ${dyA}px)`;
                    viewRef.current = { zoom, bearing, pitch, lng: center.lng, lat: center.lat };
                    return;
                }
            }

            // ❸ تغيّر التكبير/الدوران/الميل: إسقاط كامل لكل رمز. (إعادة البناء لا تمرّ
            //    من هنا: `build` يكتب المواضع من إحداثيات الفصل ثم يستدعي syncView.)
            //    الموضع يُكتب كـ transform لا left/top — الأخيرة تُجبر إعادة تخطيط لكل
            //    عنصر، بينما التحويل يبقى على مُركِّب الطبقات.
            host.style.transform = '';
            for (const m of markersRef.current) {
                const pt = mapObj.project([m.lon, m.lat]);
                m.el.style.transform = `translate(${pt.x}px, ${pt.y}px) translate(-50%, -50%)${m.suffix}`;
            }
            syncView();
        };

        /**
         * يثبّت حالة المنظور والمرساتين بعد وضعٍ كامل للرموز.
         *
         * مفصولة عن `position` كي يستدعيها `build` مباشرةً: هناك تُعرف مواضع الشاشة سلفاً
         * (حُسبت أثناء الفصل)، فلا داعي لتمريرة إسقاط ثانية على آلاف الرموز لمجرّد إعادة
         * حساب ما هو محسوب — وكان ذلك بالضبط ما يفعله نداء `position(true)` في نهاية البناء.
         */
        const syncView = () => {
            const mapObj = mapRef.current;
            if (!mapObj) return;
            const first = markersRef.current[0];
            const last = markersRef.current[markersRef.current.length - 1];
            anchorsRef.current = first && last
                ? {
                    a: { lon: first.lon, lat: first.lat, ...mapObj.project([first.lon, first.lat]) },
                    b: { lon: last.lon, lat: last.lat, ...mapObj.project([last.lon, last.lat]) },
                }
                : null;
            const c = mapObj.getCenter();
            viewRef.current = {
                zoom: mapObj.getZoom(), bearing: mapObj.getBearing(), pitch: mapObj.getPitch(),
                lng: c.lng, lat: c.lat,
            };
        };

        const build = () => {
            const mapObj = mapRef.current;
            const host = overlayRef.current;
            if (!mapObj || !host) return;

            const idx = timeIndexRef.current;
            // لحظة العرض الفعلية: الساعة idx مضافاً إليها نسبة الموضع داخلها. كل قراءة
            // أدناه تُؤخذ عند هذه اللحظة لا عند بداية الساعة، فيتطابق السرب مع اللون.
            const frac = Math.max(0, Math.min(1, fractionRef.current));
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

            /**
             * قراءة سلَّمية عند لحظة العرض: مزج خطّي بين الساعة والساعة التالية بنفس
             * النسبة التي يمزج بها شيدر الألوان نسيجَيه. إن لم يصل النسيج التالي بعد
             * نُرجع قراءة الساعة وحدها — أدقّ ما هو متاح، والبناء التالي يصحّحها.
             */
            const sampleAt = (
                type: Parameters<typeof rasterSampler.sampleScalar>[0],
                lat: number, lon: number,
            ): number | null => {
                const a = rasterSampler.sampleScalar(type, idx, lat, lon);
                if (a === null || frac <= 0) return a;
                const b = rasterSampler.sampleScalar(type, idx + 1, lat, lon);
                return b === null ? a : a + (b - a) * frac;
            };

            /** ونفس المزج لتراكم المطر — وهو الحقل الذي يُلوَّن به الهطول فعلاً. */
            const accumAt = (lat: number, lon: number): number | null => {
                const a = rasterSampler.sampleAccumulated(
                    'precipitation', idx, lat, lon, PRECIP_ACCUMULATION_HOURS,
                );
                if (a === null || frac <= 0) return a;
                const b = rasterSampler.sampleAccumulated(
                    'precipitation', idx + 1, lat, lon, PRECIP_ACCUMULATION_HOURS,
                );
                return b === null ? a : a + (b - a) * frac;
            };
            const candidates: {
                lon: number; lat: number; activity: number;
                rot: number; scale: string; delay: string;
            }[] = [];
            let waitingForData = false;
            const kIndexUnavailable = rasterSampler.isUnavailable('k-index', idx);

            const boxProbe = mapObj.getZoom() < BOX_PROBE_MAX_ZOOM;
            const half = CANDIDATE_CELL_PX / 2;

            // أركان خلايا المسح ليست مواضع مستقلّة: كلّ عمود يشترك في حدَّي خط الطول
            // نفسيهما وكلّ صفّ في حدَّي خط العرض نفسيهما. وبلا دوران ولا ميل ينفصل
            // المحوران في مركاتور (خط الطول من x وحده، خط العرض من y وحده)، فتكفي
            // (أعمدة + صفوف) عمليةَ إسقاط عكسي بدل اثنتين لكلّ خليّة — نحو ٢٠٠ بدل
            // ٢٠٠٠٠ على شاشة كاملة عند التصغير. مفاتيح الذاكرة قيم البكسل نفسها،
            // وهي متطابقة تماماً بين الخلايا لأنها تُشتقّ من الحساب ذاته.
            const separable = mapObj.getBearing() === 0 && mapObj.getPitch() === 0;
            const lonCache = new Map<number, number>();
            const latCache = new Map<number, number>();
            const lonAtX = (x: number, y: number) => {
                let v = lonCache.get(x);
                if (v === undefined) { v = mapObj.unproject([x, y]).lng; lonCache.set(x, v); }
                return v;
            };
            const latAtY = (y: number, x: number) => {
                let v = latCache.get(y);
                if (v === undefined) { v = mapObj.unproject([x, y]).lat; latCache.set(y, v); }
                return v;
            };

            forEachScatterCell(mapObj, CANDIDATE_CELL_PX, (c) => {
                if (candidates.length >= MAX_CANDIDATES) return;

                // ── أين النشاط داخل هذه الخليّة؟ ────────────────────────────────
                // K-index هو مقياس النشاط: عند التصغير نمسح رقعة الخليّة كاملةً بدقّة
                // الراستر ونأخذ أعلى قيمة وموقعها، فلا تُفقد خليّة عاصفية لمجرّد أنها
                // صارت أصغر من تباعد الشبكة على الشاشة. وعند التقريب تكفي العيّنة
                // النقطية (خليّة الشاشة أضيق من خليّة الراستر أصلاً).
                let activity: number | null;
                let lon = c.lon, lat = c.lat;
                if (boxProbe) {
                    // ركنا الخليّة على الشاشة → رقعتها الجغرافية. محور y للشاشة معكوس
                    // عن خط العرض (الأعلى = شمال)، لذا min/max صريحان لا افتراض ترتيب.
                    let nwLng: number, nwLat: number, seLng: number, seLat: number;
                    if (separable) {
                        nwLng = lonAtX(c.x - half, c.y); seLng = lonAtX(c.x + half, c.y);
                        nwLat = latAtY(c.y - half, c.x); seLat = latAtY(c.y + half, c.x);
                    } else {
                        const nw = mapObj.unproject([c.x - half, c.y - half]);
                        const se = mapObj.unproject([c.x + half, c.y + half]);
                        nwLng = nw.lng; nwLat = nw.lat; seLng = se.lng; seLat = se.lat;
                    }
                    if (!Number.isFinite(nwLat) || !Number.isFinite(seLat)) return;
                    const peak = rasterSampler.maxScalarInBoxAt(
                        'k-index', idx, frac,
                        Math.min(nwLat, seLat), Math.max(nwLat, seLat),
                        Math.min(nwLng, seLng), Math.max(nwLng, seLng),
                    );
                    if (peak === null) { if (!kIndexUnavailable) waitingForData = true; return; }
                    activity = peak.value;
                    // الرمز يقع على الذروة نفسها لا على منتصف رقعة أغلبها صافٍ.
                    lon = peak.lon;
                    lat = peak.lat;
                } else {
                    activity = sampleAt('k-index', lat, lon);
                    if (activity === null) { if (!kIndexUnavailable) waitingForData = true; return; }
                }
                if (activity < K_THRESHOLD) return;

                // ── بوّابات التأكيد، عند نقطة الذروة وحدها ──────────────────────
                // بعد K-index لا قبله: تراكم المطر ثلاث ساعات = ثلاث قراءات بيكيوبك
                // (٤٨ قراءة نسيج) لكل خليّة، وكان يُدفع ثمنها عن كل خليّة على الشاشة
                // بينما تُسقِط عتبة K-index أغلبها بعده. الترتيب الآن يقصر الثمن على
                // الخلايا التي اجتازت النشاط فعلاً.
                //
                // اشتراط الهطول جوهري لا زيادة: K-index ≥ 26 وحده يُنتج صواعق فوق
                // مناطق حارّة غير مستقرّة صافية السماء (صحاري، ظهيرة صيف بلا سحاب) —
                // القابلية موجودة لكن لا سحابة رعدية فعلاً. اشتراط ≥ 0.5 مم/3س هو
                // "توقّع مطر" مطلوب من المستخدم صراحةً: لا برق بلا مطر.
                const precipitation = accumAt(lat, lon);
                if (precipitation === null) { waitingForData = true; return; }
                if (precipitation <= MIN_STORM_MM) return;
                const surfaceTemp = sampleAt('temperature', lat, lon);
                if (surfaceTemp === null) { waitingForData = true; return; }
                if (surfaceTemp < MIN_STORM_TEMP_C) return;
                candidates.push({
                    lon, lat, activity,
                    rot: Math.round((c.r2 - 0.5) * 22),
                    scale: (0.8 + c.r3 * 0.4).toFixed(2),
                    delay: (-c.r4 * 3.2).toFixed(2),
                });
            });

            // لا نمسح سرباً موجوداً بسبب نسيج لم يكتمل تحميله بعد.
            if (waitingForData && candidates.length === 0) return;

            // ── الفصل: صاعقة واحدة لكل خليّة عاصفية ────────────────────────────
            // الأقوى أوّلاً، ثم تُرفض كل مرشّحة تقع أقرب من MIN_SEPARATION_PX إلى مرشّحة
            // مقبولة. الترتيب بالشدّة ليس تفصيلاً: هو ما يضمن أن يبقى الرمز على أقوى
            // نقطة في العاصفة لا على أوّل خليّة صادفها المسح، وأن تظهر أقوى العواصف
            // دائماً مهما ضاقت الشاشة أو ازدحمت.
            //
            // الجيران عبر جدول تجزئة مكاني بخليّة = مسافة الفصل، فيكفي فحص المربّعات
            // التسعة المجاورة بدل مقارنة كل مرشّحة بكل مقبولة (تربيعية على آلاف العناصر).
            candidates.sort((a, b) => b.activity - a.activity);
            const bucketOf = (v: number) => Math.floor(v / MIN_SEPARATION_PX);
            const taken = new Map<string, { x: number; y: number }[]>();
            const minDistSq = MIN_SEPARATION_PX * MIN_SEPARATION_PX;
            // نحتفظ بموضع الشاشة (x/y) لا بنصّ HTML: هو محسوب هنا أصلاً للفصل، ويُستعمل
            // مباشرةً كتحويل العنصر فتسقط تمريرة الإسقاط الكاملة التي كانت تليه.
            const picked: {
                lon: number; lat: number; x: number; y: number; suffix: string; delay: string;
            }[] = [];

            for (const cand of candidates) {
                if (picked.length >= MAX_MARKERS) break;
                const pt = mapObj.project([cand.lon, cand.lat]);
                const bx = bucketOf(pt.x), by = bucketOf(pt.y);
                let tooClose = false;
                for (let j = -1; j <= 1 && !tooClose; j++) {
                    for (let i = -1; i <= 1 && !tooClose; i++) {
                        const bucket = taken.get(`${bx + i}:${by + j}`);
                        if (!bucket) continue;
                        for (const p of bucket) {
                            const dx = p.x - pt.x, dy = p.y - pt.y;
                            if (dx * dx + dy * dy < minDistSq) { tooClose = true; break; }
                        }
                    }
                }
                if (tooClose) continue;
                const key = `${bx}:${by}`;
                const bucket = taken.get(key);
                if (bucket) bucket.push({ x: pt.x, y: pt.y });
                else taken.set(key, [{ x: pt.x, y: pt.y }]);

                picked.push({
                    lon: cand.lon,
                    lat: cand.lat,
                    x: pt.x,
                    y: pt.y,
                    suffix: ` rotate(${cand.rot}deg) scale(${cand.scale})`,
                    delay: `${cand.delay}s`,
                });
            }

            // ── تحديث الـ DOM بإعادة استعمال العناصر ───────────────────────────
            // لا `innerHTML` بعد الآن. كان كل تكبير/تصغير يهدم السرب كاملاً ويُعيد تحليل
            // نصٍّ بحجم ميغابايت لبناء عشرات آلاف العقد من جديد — وهو مصدر البطء الظاهر
            // مباشرةً. عدد الصواعق بين بناءين متقارب جداً في العادة (نفس الشاشة، تكبير
            // مجاور)، فنُبقي العناصر الموجودة ونكتب عليها المواضع الجديدة فقط: لا هدم
            // ولا تحليل ولا تخصيص، والإضافة/الحذف يقتصران على الفرق.
            const animate = !reduceMotion && picked.length <= MAX_ANIMATED;
            host.className = animate ? 'lightning-host is-animated' : 'lightning-host';
            host.style.transform = '';

            const pool = markersRef.current;
            while (pool.length > picked.length) pool.pop()!.el.remove();
            // العناصر الجديدة تُجمَّع في جزء منفصل ثم تُلحَق دفعةً واحدة.
            const fragment = pool.length < picked.length ? document.createDocumentFragment() : null;
            for (let i = 0; i < picked.length; i++) {
                const p = picked[i];
                let rec = pool[i];
                if (!rec) {
                    const el = document.createElement('div');
                    el.className = 'lightning-marker';
                    fragment!.appendChild(el);
                    rec = { el, lon: 0, lat: 0, suffix: '', delay: '' };
                    pool.push(rec);
                }
                rec.lon = p.lon;
                rec.lat = p.lat;
                rec.suffix = p.suffix;
                // تأخير الوميض مشتقّ من تجزئة الخليّة، فيتكرّر كما هو عند أغلب العناصر
                // المُعاد استعمالها — والكتابة على النمط أغلى من المقارنة بكثير.
                if (rec.delay !== p.delay) { rec.delay = p.delay; rec.el.style.animationDelay = p.delay; }
                rec.el.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)${p.suffix}`;
            }
            if (fragment) host.appendChild(fragment);

            // المواضع كُتبت أعلاه من إحداثيات الفصل نفسها، فيبقى تثبيت حالة المنظور فقط.
            syncView();
        };

        // نقيس كلفة كل بناء فعلياً: معدّل الملاحقة أدناه يُعاير عليها لا على رقم ثابت.
        rebuildRef.current = () => {
            const t0 = performance.now();
            build();
            const ms = performance.now() - t0;
            buildMsRef.current = buildMsRef.current === 0 ? ms : buildMsRef.current * 0.7 + ms * 0.3;
        };
        // مغلّفة عمداً: MapLibre يمرّر كائن الحدث كأوّل وسيط، فلا نمرّر `position` مباشرةً
        // كي لا يتسرّب وسيط غير متوقَّع إليها لو أضيف لها معامل يوماً.
        const onRender = () => position();
        map.on('render', onRender);

        return () => {
            map.off('render', onRender);
            rebuildRef.current = null;
            overlay.remove();
        };
    }, [mapRef]);

    // ── إعادة بناء السرب عند التفعيل/تحريك الخريطة ────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const scheduleRebuild = () => {
            if (rafRef.current !== null) return;
            rafRef.current = window.requestAnimationFrame(() => {
                rafRef.current = null;
                rebuildRef.current?.();
            });
        };

        if (!enabled) {
            overlayRef.current && (overlayRef.current.innerHTML = '');
            markersRef.current = [];
            return;
        }

        scheduleRebuild();
        map.on('moveend', scheduleRebuild);
        map.on('zoomend', scheduleRebuild);
        return () => {
            map.off('moveend', scheduleRebuild);
            map.off('zoomend', scheduleRebuild);
            if (rafRef.current !== null) { window.cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        };
    }, [mapRef, enabled]);

    // لا نكتفي بإحماء k-index: فالتصفية تستخدم تراكم المطر لثلاث ساعات. كان ذلك يعني
    // أن البرق ينتظر تحميل ثلاثة إطارات مطر ضمنياً، ثم لا يعاد بناؤه إلا بعد مؤقت قصير.
    // ننتظر الحقول المطلوبة فعلياً ونبني السرب فور وصولها.
    useEffect(() => {
        if (!enabled) return;

        let cancelled = false;
        // رقم محاولة: نتيجة تحميل بدأ لنموذج ثم بُدّل النموذج قبل وصولها تُهمَل، فلا
        // يُبنى السرب من حقول أوشكت أن تُفرَغ.
        let attempt = 0;

        const loadAndRebuild = () => {
            const mine = ++attempt;
            // الساعة التالية مطلوبة كالحالية تماماً: المزج الزمني يقرأ منها في كل نسبة
            // فوق الصفر. وبلا تحميلها مسبقاً يعمل المزج على الإطار الحالي وحده طوال
            // انتقال الشريط ثم يقفز عند وصولها — وهو التأخّر نفسه بصورة أخرى.
            for (const i of [timeIndex, timeIndex + 1]) {
                rasterSampler.prefetch('k-index', i);
                rasterSampler.prefetch('precipitation', i);
                rasterSampler.prefetch('temperature', i);
            }

            // تراكم ٣ ساعات عند الساعة التالية يحتاج نافذتها هي أيضاً، فتمتدّ إلى +1.
            const requiredRainFrames = [timeIndex + 1, timeIndex, timeIndex - 1, timeIndex - 2]
                .filter((idx) => idx >= 0);
            void Promise.all([
                rasterSampler.ensure('k-index', timeIndex),
                rasterSampler.ensure('temperature', timeIndex),
                ...requiredRainFrames.map((idx) => rasterSampler.ensure('precipitation', idx)),
            ]).then(() => {
                if (!cancelled && mine === attempt) rebuildRef.current?.();
            });
            // الساعة التالية لا تُنتظَر: انتظارها يؤخّر أوّل رسم للساعة الحالية بلا داعٍ،
            // فالمزج يسقط عليها وحدها حتى تصل ثم يُصحَّح في البناء التالي.
            void Promise.all([
                rasterSampler.ensure('k-index', timeIndex + 1),
                rasterSampler.ensure('temperature', timeIndex + 1),
            ]).then(() => {
                if (!cancelled && mine === attempt) rebuildRef.current?.();
            });
        };

        loadAndRebuild();

        // طبقات اللون تأخذ رابط النموذج خاصيةً من React فتتبدّل فوراً، أمّا هذه الطبقة
        // فتقرأ من كاش rasterSampler — ولا شيء في تبديل النموذج يُعلمها أن الكاش أُفرغ
        // تحتها. فكانت تبقى على سرب النموذج السابق حتى يوقظها حدث لا علاقة له بالتبديل
        // (تحريك الخريطة أو تبديل الساعة)، وهو سبب تأخّر الصواعق ثوانيَ خلف الألوان.
        const unsubscribe = rasterSampler.onDirChange(loadAndRebuild);

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [enabled, timeIndex, modelName]);

    // ── ملاحقة حركة الشريط داخل الساعة ────────────────────────────────────────
    // الأثر أعلاه يُطلق عند تغيّر **الفهرس** فقط، وهو ما كان يجعل السرب يقفز بالساعة
    // بينما اللون ينساب. هنا نعيد البناء على خطوات النسبة أيضاً، فيلاحق السرب اللون
    // بدل أن ينتظر الفهرس التالي. النسيج مُحمَّل سلفاً في هذه الحالة فالبناء حسابٌ محض
    // بلا شبكة. (نطرح الفهرس من الشرط: تغيّره يتكفّل به الأثر أعلاه بتحميله وإعادة بنائه.)
    const fractionStep = Math.round(Math.max(0, Math.min(1, frameFraction)) * FRACTION_STEPS);
    useEffect(() => {
        if (!enabled) return;
        /*
         * ── سقف زمني لإعادة البناء، لا سقف خطوات ────────────────────────────────
         *
         * بناء السرب ليس رخيصاً: يمسح نافذة العرض بخلايا 14 بكسل — على شاشة عريضة
         * آلاف الخلايا — ويقرأ لكلٍّ ذروة k-index وتراكم الهطول. الكلفة ثابتة لكل
         * إعادة بناء، فما يحكم الثقل هو **عددها في الثانية** لا عددها في الساعة.
         *
         * وقد كان ذلك مقبولاً عند 3000 مللي للساعة (إعادة كل ~500 مللي)، لكن تسريع
         * التشغيل إلى 900 يجعلها كل ~150 مللي — أي ثلاثة أضعاف الحمل على الخيط
         * الرئيسي في اللحظة التي نريدها فيها أخفّ ما يكون.
         *
         * فنحدّ المعدّل بالزمن الحقيقي: أربع إعادات في الثانية كحدّ أقصى. السرب
         * يلاحق اللون بفارق لا يتجاوز ربع ثانية — غير مُدرَك بصرياً — والكلفة تصير
         * مستقلّة عن سرعة التشغيل بدل أن تتضاعف معها.
         *
         * ولا يُسقَط الطلب الأخير: يُؤجَّل بمؤقّت فيصل السرب إلى موضعه الصحيح حتى لو
         * توقّف التشغيل بين إعادتين.
         */
        /*
         * ── المعدّل يُعاير على الكلفة المقيسة، لا على رقم ثابت ──────────────────
         *
         * كان الحدّ 250 مللي ثابتاً: على أي جهاز، وأي حجم شاشة، وأي سرعة تشغيل. فكان
         * السرب متأخّراً ربع ثانية عن اللون دائماً — حتى حين يكون البناء 8 مللي ولا
         * يوجد أي سبب للتأخّر — ويظلّ ثقيلاً حين يكون 60 مللي على شاشة عريضة.
         *
         * الصحيح أن نحكم **نسبة الخيط الرئيسي** المصروفة على الملاحقة لا عددها: نسمح
         * للبناء بأن يأخذ خُمس الزمن بين بنائين (DUTY = 0.2)، فالفاصل = الكلفة ÷ 0.2.
         * بناء 8 مللي ⇒ فاصل 60 مللي (16 مرّة/ثانية: ملاحقة لا يُدرَك تأخّرها)، وبناء
         * 60 مللي ⇒ 300 مللي (تباطؤ واعٍ يحمي إطارات الرسم). والحدّان يمنعان الطرفين:
         * لا أسرع من إطار رسم واحد (60 مللي)، ولا أبطأ من 400 مللي.
         */
        const DUTY = 0.2;
        const measured = buildMsRef.current;
        const minRebuildMs = measured === 0
            ? 120                                                    // قبل أول قياس: وسط محافظ
            : Math.max(60, Math.min(400, measured / DUTY));
        const since = performance.now() - lastRebuildRef.current;
        const run = () => {
            lastRebuildRef.current = performance.now();
            rebuildRef.current?.();
        };
        if (since >= minRebuildMs) {
            // نمرّ على نفس مُجدوِل rafRef الذي تستعمله أحداث الخريطة: سحب الشريط قد يطلق
            // خطوات أسرع من إطار الرسم، وبلا تجميعها نبني السرب مرّتين لإطار واحد يُرسم.
            if (rafRef.current !== null) return;
            rafRef.current = window.requestAnimationFrame(() => {
                rafRef.current = null;
                run();
            });
            return;
        }
        const t = window.setTimeout(run, minRebuildMs - since);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, fractionStep]);

    return null;
}
